import MakeQuerablePromise from './MakeQuerablePromise';

let requests = {};

/**
 * Stores the request in the requests variable.
 * @param {API} request
 */
function addRequest(request) {
  if (!requests[request._method]) {
    requests[request._method] = {};
  }
  if (!requests[request._method][request._endpoint]) {
    requests[request._method][request._endpoint] = [];
  }
  requests[request._method][request._endpoint].push(request);
}

/**
 * Checks to see if the request has already been made and cached.
 * @param {String} method The method of the request.
 * @param {String} endpoint The endpoint of the request.
 * @param {Object|Array|FormData} args Currently the caching of args is not available, so this should be an empty object.
 * @returns {API|false} False if it cannot find the request, the API object if it can be found.
 */
function findRequest(method, endpoint, args = []) {
  if (requests[method] && requests[method][endpoint]) {
    return requests[method][endpoint][0];
  }
  return false;
}

/**
 * Used to query the API.
 * @class API
 */
export default class API {
  /**
   * @param {String} method The method used to fetch the data from the API.
   * @param {String} endpoint The endpoint to fetch the data from.
   * @param {Array} args The arguments passed to the API.
   * @param {Boolean} cacheable Should the response be cached and reused.
   * @private
   */
  constructor(method, endpoint, args = {}, cacheable = false) {
    /**
     * @param {String} The method used to fetch the data from the API.
     * @private
     */
    this._method = method;
    /**
     * The endpoint the API is querying.
     * @type {String}
     * @private
     */
    this._endpoint = endpoint;
    /**
     * Arguments to the API
     * @type {Object|FormData}
     * @private
     */
    this._args = args;
    /**
     * Should the response be cached.
     * @type {Boolean}
     * @private
     */
    this._cacheable = cacheable;

    /**
     * The promise from the request.
     * @type {Promise|null}
     * @private
     */
    this._promise = null;

    if (this.isCacheable()) {
      addRequest(this);
    }
  }

  /**
   * Check to see if the request can be cached. If the request has arguments it cannot be cached. If the arguments is an
   * empty object or array this is allowed.
   * @returns {Boolean}
   */
  isCacheable() {
    return this._cacheable && (!this.args || (!(this._args instanceof FormData) && (this._args instanceof Array ? this._args.length : Object.keys(this._args).length) <= 0));
  }

  /**
   * Send the request.
   * @return {Promise}
   */
  send() {
    if (this._promise !== null && !MakeQuerablePromise(this._promise).isRejected()) {
      // If the promise has been successfully completed just return it, no need to do it again, return the same promise.
      return this._promise;
    }

    const url = API.getAPIBaseUrl() + this._endpoint;

    // Use the Promise object as an event listener.
    this._promise = new Promise((resolve, reject) => {
      let xhr = new XMLHttpRequest();
      let t = this;

      xhr.open(this._method, url, true);
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          let response;

          try {
            response = JSON.parse(xhr.responseText);
          } catch (e) {
            response = xhr.responseText;
          }

          if (xhr.status === 200) {
            // Request successful report this.
            resolve(response, xhr);
          } else {
            // Promise rejection requires an error object, attaching extra field which can be used to handle the error.
            let error = new Error('Failed to fetch response');
            error.status = xhr.status;
            error.response = response;
            error.xhr = xhr;
            reject(error);
          }
        }
      };
      // If the arguments is in the form of a FormData object send it with the request.
      if (t._args instanceof FormData) {
        xhr.send(this._args);
      } else {
        xhr.send();
      }
    });

    return this._promise;
  }

  /**
   * @returns {String} The API base URL, set in the .env file.
   * @private
   */
  static getAPIBaseUrl() {
    return process.env.API_BASE_URL;
  }

  /**
   * Make a request to the API.
   * @param {String} method The method used to fetch the url (GET/POST/PUT/...)
   * @param {String} endpoint The endpoint for the api request
   * @param {Object|FormData} args Arguments to send to the API.
   * @returns {API}
   * @private
   */
  static request(method, endpoint, args = {}, cachable = false) {
    let existingRequest = findRequest(method, endpoint, args);
    if (existingRequest !== false) {
      return existingRequest;
    }
    return new API(method, endpoint, args, cachable);
  }
}



// WEBPACK FOOTER //
// ./src/class/API.js